/* ═══════════════════════════════════════════════════════════════
   The Designer — the canvas.

   One continuous editable document, styled as the forma's paper.
   Click anywhere and type. The caret, selection, undo history, IME
   and clipboard belong to ProseMirror; the spec rules on top belong
   to numbering.ts and commands.ts. Nothing here is hand-rolled.

   R5 — NO MODES. The body is always a text editor and the header,
   footer, pre-body and post-body bands are always zone objects: click
   a band to edit it, drag tokens onto it, right there, while the caret
   is still sitting in a paragraph. Clicking body text NEVER means
   anything but "put the caret here."
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import type { ContentBlock, RevisionSettings, StandardConfig } from '../../shared/types';
import { DEFAULT_REVISION_SETTINGS } from '../../shared/types';
import { ySyncPluginKey } from 'y-prosemirror';
import { DESIGNER_NODES, blocksToDoc, docToBlocks } from './schema';
import {
  collabExtensions,
  withoutHistory,
  type SpecCanvasCollab,
} from '../spec-studio/collabBinding';
import { Numbering, REBUILD_LAYOUT } from './numbering';
import { Revisions, REBUILD_REVISIONS } from './revisions';
import { SpecKeymap, UniqueBlockIds } from './commands';
import { DesignerSearch } from './search';
import { SmartBlockView, SpecImageView } from './nodeviews';
import type { RelatedSectionsHost } from './relatedSectionsStore';
import { NodeSelection, type Transaction } from '@tiptap/pm/state';
import { RELATED_SECTIONS_KIND } from '../../shared/related-sections';
import { TableGrips } from './TableGrips';
import { selectCellAtCoords } from './tableCommands';
import { getUiScale, subscribeUiScale } from '../lib/uiScale';
import { probeClipboardHasContent } from './clipboardProbe';
import { BlockMenu, type BlockModuleMenuProps, type BlockMenuState } from './BlockMenu';
import { ModuleStamps, stampOfNode } from './moduleInsert';
import type { FormaReviewHighlights, FormaReviewNote } from '../review/formaHighlights';
import {
  EndOfSection,
  PageBreakPortals,
  PagedBlankPage,
  ZoneBand,
  ZoneBandEdit,
  ZoneShell,
  bandVariantName,
  createPageBreakRegistry,
  pageBreaksKey,
  pageBreaksPlugin,
  paperVars,
  proseMirrorPagerSurface,
  setPageBreaks,
  usePagination,
  readZone,
  shouldClearOnEmptyClick,
  useTokenMarquee,
  zoneKeyFor,
  type BandKind,
  type DesignWiring,
  type ZoneColumn,
} from '../forma-studio';

/** What y-prosemirror stamps on a transaction it made FROM the shared
 *  document. A remote peer's edit is not this user's - it must not restart
 *  this window's JSON mirror (every peer writing the same text would race).
 *  Undo/redo ARE this user's edits. */
function isRemoteSharedChange(tr: Transaction): boolean {
  const m = tr.getMeta(ySyncPluginKey) as
    | { isChangeOrigin?: boolean; isUndoRedoOperation?: boolean }
    | undefined;
  return m?.isChangeOrigin === true && m.isUndoRedoOperation !== true;
}

export interface DesignerCanvasProps {
  /** A Sync review is open — module colours step aside (.is-reviewing). */
  reviewing?: boolean;
  /** Identity of the loaded document — changing it reloads the canvas.
   *  Viewing a snapshot passes a different key than live editing, so the
   *  undo histories never bleed into each other. */
  specId: string;
  blocks: ContentBlock[];
  forma: StandardConfig | null;
  specNumber: string;
  specName: string;
  projectMeta: Record<string, string>;
  zoom: number;
  /** Ctrl+mouse-wheel zoom (Cohesion R1 Phase 4 zoom law — every zoomable
   *  surface responds). `step` is a zoom delta already in the same 0–1
   *  fraction `zoom` uses; the caller clamps to its own range. */
  onZoomDelta?: (step: number) => void;
  /** Fired on every document change, already converted to storage shape. */
  onChange: (blocks: ContentBlock[]) => void;
  /** Ctrl+S. */
  onSave: () => void;
  /** Escape left the document — move focus somewhere sensible in the shell. */
  onEscape?: () => void;
  /** So the shell can drive undo/redo and read `can()` state. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Read-only when viewing history or without edit permission. */
  editable?: boolean;
  /** Zone wiring. Present → the paper's bands are live objects (click to
      select, drop tokens with snapping columns). The BODY is unaffected: it
      is a text editor either way. Absent → a read-only paper (viewing an
      issuance, or no edit rights). */
  design?: DesignWiring;
  /** Right-click ▸ "Level style…" — hand the level to the Levels palette. */
  onLevelStyle?: (level: number) => void;
  /** Right-click on a block a module insert put here (Phase 5) — select or
   *  delete that insertion as a unit. */
  moduleMenu?: BlockModuleMenuProps | null;
  /** The revision-mark baseline (flattened printable blocks). Null = design
   *  phase / no baseline — no marks render. */
  baselineBlocks?: ContentBlock[] | null;
  /** The architect's punch-in mark styles (migration 033). */
  revisionSettings?: RevisionSettings;
  /** The live feed behind Related sections blocks (RelatedSections.tsx).
   *  Absent → they render as the master placeholder. */
  relatedSections?: RelatedSectionsHost | null;
  /** Right-click ▸ "Insert related sections". Absent → no such item. */
  onInsertRelatedSections?: () => void;
  /** LIVE SHARED DOCUMENT (docs/SPEC_WORKFLOW.md section 5). Present => the
   *  document IS the shared Yjs fragment: `blocks` is ignored, History is
   *  swapped for the Yjs undo manager (Ctrl+Z undoes only YOUR edits) and
   *  peers' carets show. Part of the editor's identity, like `specId`. */
  collab?: SpecCanvasCollab | null;
  /** Review of a project forma's master updates (SYNC_PLAN §3.2): the
   *  changed bands glow, the changed levels light, and the change being
   *  walked shows its old value struck and the new one added. */
  reviewHighlights?: FormaReviewHighlights | null;
  /** Workspace ▸ View ▸ Pages: draw the document as true-size pages, each
   *  with its own header and footer. Display only — never a document change.
   *  Off (the default) is the continuous sheet, exactly as before. */
  paged?: boolean;
}

/** The change being walked, on the paper: text-bearing settings strike the
 *  old tokens and add the new; style settings show before → after. */
function FormaReviewNoteView({ note }: { note: FormaReviewNote }) {
  return (
    <div className="sf-review-forma-note" role="note" aria-label={note.label}>
      <span className="sf-review-forma-label">{note.label.replace(/:.*$/, '')}</span>
      {note.text ? (
        <span className="sf-review-forma-values">
          {note.before && <span className="sf-review-removed">{note.before}</span>}
          {note.after && <span className="sf-review-added">{note.after}</span>}
        </span>
      ) : (
        <span className="sf-review-forma-values">
          <span className="sf-review-forma-swatch sf-review-removed">{note.before || 'None'}</span>
          <span aria-hidden="true">→</span>
          <span className="sf-review-forma-swatch sf-review-added">{note.after || 'None'}</span>
        </span>
      )}
      {note.oursText != null && (
        <span className="sf-review-forma-ours">This project has: {note.oursText || 'None'}</span>
      )}
    </div>
  );
}

export function DesignerCanvas({
  reviewing,
  specId,
  blocks,
  forma,
  specNumber,
  specName,
  projectMeta,
  zoom,
  onZoomDelta,
  onChange,
  onSave,
  onEscape,
  onEditorReady,
  editable = true,
  design,
  onLevelStyle,
  moduleMenu,
  baselineBlocks = null,
  revisionSettings,
  relatedSections = null,
  onInsertRelatedSections,
  collab = null,
  reviewHighlights = null,
  paged = false,
}: DesignerCanvasProps) {
  // UI Scale dial (DESIGN.md §3.6): `zoom` is the RELATIVE percent (1.0 =
  // matches surroundings); the applied transform is relative × dial.
  const [dial, setDial] = React.useState(() => getUiScale().dial);
  React.useEffect(() => subscribeUiScale((v) => setDial(v.dial)), []);
  // One surface: the body is a text editor whenever the user may edit it,
  // regardless of what the zone bands are doing at the same moment.
  const canType = editable;
  // The plugins read the forma through a ref so a forma edit restyles the
  // paper without tearing down and rebuilding the editor (which would drop
  // the caret and the undo history).
  const formaRef = React.useRef<StandardConfig | null>(forma);
  formaRef.current = forma;

  // Same pattern for the revision baseline + punch-in styles: a settings
  // tweak restyles the marks without rebuilding the editor.
  const baselineRef = React.useRef<ContentBlock[] | null>(baselineBlocks);
  baselineRef.current = baselineBlocks;
  const settingsRef = React.useRef<RevisionSettings>(revisionSettings || DEFAULT_REVISION_SETTINGS);
  settingsRef.current = revisionSettings || DEFAULT_REVISION_SETTINGS;

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;
  const onEscapeRef = React.useRef(onEscape);
  onEscapeRef.current = onEscape;
  const relatedRef = React.useRef(relatedSections);
  relatedRef.current = relatedSections;

  const editor = useEditor(
    {
      extensions: [
        // A shared document's undo is the Yjs undo manager; its binding and
        // the peers' carets come from the same helper the Spec Studio uses.
        ...(collab ? withoutHistory(DESIGNER_NODES) : DESIGNER_NODES),
        ...(collab ? collabExtensions(collab) : []),
        DesignerSearch,
        UniqueBlockIds,
        Numbering.configure({ getForma: () => formaRef.current }),
        // Blocks that arrived by inserting a module wear a quiet rail, so an
        // insertion reads as one thing (Phase 5) — the SAME extension the
        // Spec Masters Studio installs, so it looks identical in both.
        ModuleStamps,
        Revisions.configure({
          getBaseline: () => baselineRef.current,
          getSettings: () => settingsRef.current,
        }),
        SpecKeymap.configure({
          onSave: () => onSaveRef.current(),
          onEscape: () => onEscapeRef.current?.(),
        }),
      ],
      // Shared: the Yjs fragment is the content (Collaboration renders it).
      content: collab ? undefined : blocksToDoc(blocks),
      editable: canType,
      editorProps: {
        attributes: {
          class: 'dsg-doc',
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': `${specNumber} ${specName} — specification text`,
        },
        nodeViews: {
          // The table's NodeView is wired in automatically by the framework's
          // own columnResizing plugin (tables.ts's SpecTable sets `View:`) —
          // nothing to register here.
          specImage: (node) => new SpecImageView(node),
          smartBlock: (node) => new SmartBlockView(node, relatedRef.current),
        },
        // Enter on a selected Related sections block moves focus into its
        // picker — the keyboard path to what a click opens.
        handleKeyDown: (view, event) => {
          const host = relatedRef.current;
          const sel = view.state.selection;
          if (!host || event.key !== 'Enter' || !(sel instanceof NodeSelection)) return false;
          const meta = sel.node.attrs.meta as Record<string, unknown> | undefined;
          if (sel.node.type.name !== 'smartBlock' || meta?.smartBlockKind !== RELATED_SECTIONS_KIND)
            return false;
          const dom = view.nodeDOM(sel.from);
          if (!(dom instanceof HTMLElement)) return false;
          host.open((sel.node.attrs.blockId as string) || '', dom, true);
          return true;
        },
      },
      onUpdate: ({ editor: ed, transaction }) => {
        if (!transaction.docChanged) return;
        if (collab && isRemoteSharedChange(transaction)) return;
        onChangeRef.current(docToBlocks(ed.getJSON()));
      },
    },
    // Rebuilding on specId is deliberate: switching sections IS a new
    // document, and its undo history must not bleed across sections.
    [specId, collab?.ydoc]
  );

  React.useEffect(() => {
    onEditorReady?.(editor ?? null);
    return () => onEditorReady?.(null);
  }, [editor, onEditorReady]);

  React.useEffect(() => {
    if (editor) editor.setEditable(canType);
  }, [editor, canType]);

  // ─── Pages view (display only) ───
  // The break decorations live in a plugin that exists ONLY while Pages is
  // on, appended after every other plugin (peers' carets keep drawing).
  const sheetRef = React.useRef<HTMLDivElement | null>(null);
  const pageRegistry = React.useMemo(() => createPageBreakRegistry(), []);
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !paged) return;
    if (!pageBreaksKey.getState(editor.state))
      editor.registerPlugin(pageBreaksPlugin(pageRegistry));
    return () => {
      if (!editor.isDestroyed) editor.unregisterPlugin(pageBreaksKey);
    };
  }, [editor, paged, pageRegistry]);
  const pagerSurface = React.useMemo(
    () => (editor ? proseMirrorPagerSurface(editor, () => formaRef.current) : null),
    [editor]
  );
  const { pages } = usePagination({
    enabled: paged && !!editor,
    forma,
    sheetRef,
    surface: pagerSurface,
  });
  React.useEffect(() => {
    if (!editor || editor.isDestroyed || !paged) return;
    const draw = () => {
      if (editor.isDestroyed || !pageBreaksKey.getState(editor.state)) return;
      editor.view.dispatch(setPageBreaks(editor.state.tr, pages));
    };
    // Never redraw under an IME composition — it would cut the word short.
    // The breaks land the moment the composition ends.
    if (!editor.view.composing) {
      draw();
      return;
    }
    const dom = editor.view.dom;
    const onEnd = () => requestAnimationFrame(draw);
    dom.addEventListener('compositionend', onEnd, { once: true });
    return () => dom.removeEventListener('compositionend', onEnd);
  }, [editor, paged, pages]);
  // The last page that carries text (the blank page, when it fires, follows).
  const lastTextPage = pages.numPages - (pages.blankPage ? 1 : 0);

  // A forma change restyles every paragraph — ask the layout plugin to
  // re-derive without touching the document.
  React.useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta(REBUILD_LAYOUT, true));
  }, [editor, forma]);

  // A baseline or punch-in change re-derives the marks the same way.
  React.useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta(REBUILD_REVISIONS, true));
  }, [editor, baselineBlocks, revisionSettings]);

  // ─── Right-click on a paragraph ───
  const [menu, setMenu] = React.useState<BlockMenuState | null>(null);
  // The clipboard is probed first (80ms cap) so Paste never flashes.
  const openMenu = (s: BlockMenuState) => {
    void probeClipboardHasContent().then((has) => setMenu({ ...s, clipboardHas: has }));
  };
  const onContextMenu = (e: React.MouseEvent) => {
    if (!editor) return;
    const block = (e.target as HTMLElement).closest('[data-block-id]');
    if (!block) return;
    e.preventDefault();
    // In a table the menu's table commands act on the CELL under the
    // pointer, so the selection goes there first (Word's rule). A multi-cell
    // selection the click lands inside survives — merge needs it.
    if ((e.target as HTMLElement).closest('td,th')) {
      selectCellAtCoords(editor, e.clientX, e.clientY);
    }
    const blockId = block.getAttribute('data-block-id') || '';
    let moduleStamp = null as BlockMenuState['moduleStamp'];
    editor.state.doc.forEach((node) => {
      if (node.attrs.blockId === blockId) moduleStamp = stampOfNode(node);
    });
    openMenu({
      x: e.clientX,
      y: e.clientY,
      level: Number(block.getAttribute('data-level')) || 1,
      excluded: block.classList.contains('dsg-excluded'),
      blockId,
      moduleStamp,
    });
  };

  // Ctrl+wheel zoom (Cohesion R1 Phase 4 zoom law), ported verbatim from the
  // forma-studio canvas's wheelZoomRef. A native, non-passive listener —
  // React's synthetic onWheel is passive, so preventDefault can't stop the
  // browser's own page-zoom there.
  const wheelZoomRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !onZoomDelta) return;
      el.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          if (!e.ctrlKey) return;
          e.preventDefault();
          onZoomDelta(e.deltaY < 0 ? 0.05 : -0.05);
        },
        { passive: false }
      );
    },
    [onZoomDelta]
  );

  const paperContext = { forma, specNumber, specName, projectMeta };

  // The bands, exactly as Forma Studio's sheet draws them: live objects
  // (ZoneBandEdit — click, Ctrl/Shift-click, drag onto a slot, right-click,
  // ×, Ctrl+Left/Right) whenever the paper is live; plain text otherwise.
  // Pages view: the sheet's own header is page 1's, its footer the last
  // text page's. Continuous passes nothing (exactly as before).
  const pageProps = (kind: BandKind) =>
    paged ? { pageNum: kind === 'footer' ? lastTextPage : 1, numPages: pages.numPages } : {};
  const band = (kind: BandKind, variant?: 'First' | 'Odd' | 'Even') =>
    design ? (
      <ZoneBandEdit
        kind={kind}
        variant={variant}
        {...pageProps(kind)}
        edit={design.edit}
        onRequestTextEdit={design.onRequestTextEdit}
        onZoneInsertAt={design.onZoneInsertAt}
        onZoneReplaceAt={design.onZoneReplaceAt}
        onNotice={design.onNotice}
        {...paperContext}
      />
    ) : (
      <ZoneBand kind={kind} variant={variant} {...pageProps(kind)} {...paperContext} />
    );

  // Marquee select over the bands (Forma Studio's gesture). It never starts
  // in the document text — a drag there is a text selection — and a plain
  // click on empty paper clears the whole selection (never sticky, §3.4).
  const { beginMarquee, marqueeNode } = useTokenMarquee({
    onEmptyClick: () => design?.clearSelection?.(),
    exclude: '.ProseMirror',
  });

  // Which page variant's bands the paper shows — the one being edited. With
  // no zone wiring (read-only paper) it falls back to page 1's, as before.
  const bandVariant = design ? bandVariantName(design.pageVariant) : undefined;

  const bandEmpty = React.useCallback(
    (band: BandKind): boolean => {
      if (!forma || !design) return false;
      return (['L', 'C', 'R'] as ZoneColumn[]).every(
        (c) => readZone(forma, zoneKeyFor(band, design.pageVariant, c)).length === 0
      );
    },
    [forma, design]
  );

  // Clicking anywhere that is NOT a band drops the zone selection — one
  // selection system, never sticky (DESIGN §3.4). It does nothing else:
  // the click still places the caret, because that is all a click on body
  // text has ever been allowed to mean.
  const onSheetMouseDown = (e: React.MouseEvent) => {
    if (!design) return;
    const inText = !!(e.target as HTMLElement).closest('.ProseMirror');
    if (inText && shouldClearOnEmptyClick(e)) design.clearSelection?.();
  };

  // Review: a changed band glows; the walked change's note rides it.
  const hl = reviewHighlights;
  const reviewBand = (band: BandKind, node: React.ReactNode) => {
    if (!hl || !hl.bands.includes(band)) return node;
    const cur = hl.current?.band === band ? hl.current : null;
    return (
      <div className={`sf-review-forma-band${cur ? ' sf-review-current' : ''}`}>
        {node}
        {cur && <FormaReviewNoteView note={cur} />}
      </div>
    );
  };
  // A setting with no single band (a level, a page setting) shows its note
  // at the top of the sheet.
  const floatingNote = hl?.current && !hl.current.band ? hl.current : null;

  const wrapBand = (band: BandKind, node: React.ReactNode) =>
    design ? (
      <ZoneShell band={band} design={design} empty={bandEmpty(band)}>
        {reviewBand(band, node)}
      </ZoneShell>
    ) : (
      reviewBand(band, node)
    );

  const sheetClasses = [
    'dsg-sheet',
    paged ? 'dsg-sheet--paged' : '',
    reviewing ? 'is-reviewing' : '',
    design ? 'dsg-zones-live' : '',
    // The Levels palette points at a level → every paragraph of it lights up,
    // so "what am I restyling" is never a guess.
    design?.levelFocus ? `dsg-sel-l${design.levelFocus}` : '',
    ...(hl?.levels || []).map((n) => `sf-review-l${n}`),
    hl?.current?.level ? `sf-review-lcur${hl.current.level}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="dsg-canvas"
      style={{ ...paperVars(forma) }}
      ref={wheelZoomRef}
      onMouseDown={(e) => {
        // Click-away on the paper (outside the text) — a marquee if it drags,
        // a clear if it doesn't.
        // Portalled menus bubble here through React but are not on the paper.
        if (design && e.currentTarget.contains(e.target as Node)) beginMarquee(e);
      }}
    >
      <div
        ref={sheetRef}
        className={sheetClasses}
        onContextMenu={onContextMenu}
        onMouseDown={onSheetMouseDown}
        style={{ transform: `scale(${zoom * dial})`, transformOrigin: 'top center' }}
      >
        {floatingNote && <FormaReviewNoteView note={floatingNote} />}
        <div className="dsg-sheet-header">{wrapBand('header', band('header', bandVariant))}</div>

        <div className={`dsg-sheet-body${design?.eosSelected ? ' sfs-eos-selected' : ''}`}>
          {wrapBand('pre', band('pre'))}
          <EditorContent editor={editor} />
          {wrapBand('post', band('post'))}
          <EndOfSection {...paperContext} onConfigure={design?.onEndOfSection} />
        </div>

        <div className="dsg-sheet-footer">{wrapBand('footer', band('footer', bandVariant))}</div>
        {paged && pages.blankPage && forma && <PagedBlankPage forma={forma} />}
      </div>
      {paged && (
        <PageBreakPortals registry={pageRegistry} paper={paperContext} numPages={pages.numPages} />
      )}

      {menu && editor && (
        <BlockMenu
          editor={editor}
          state={menu}
          canEdit={editable}
          onLevelStyle={onLevelStyle}
          moduleMenu={moduleMenu ?? null}
          onInsertRelatedSections={onInsertRelatedSections}
          onClose={() => {
            setMenu(null);
            editor.commands.focus();
          }}
        />
      )}

      {/* Hover "+" affordances at the row/column edges — the framework's own
          addRowAfter / addColumnAfter, one click away (Phase 1 item 3). */}
      {editor && <TableGrips editor={editor} canEdit={editable} />}
      {marqueeNode}
    </div>
  );
}
